<template>
    <GLText
        ref="refText"
        :text="text"
        :layer="layer"
        :options="textOptions"
        @sync="onTextSync"
    ></GLText>
</template>

<script setup>
    import { Mesh } from 'three';
    import { computed, reactive, ref, watchEffect } from 'vue';

    import { simpleMaterial, simplePlaneGeometry } from '@resn/gozer-three';
    import { useThreeObject } from '@resn/gozer-vue';
    import { gsap } from '@resn/gsap';

    import { useBounds } from '~/components/gl/Bounds';
    import GLText from '~/components/gl/GLText.vue';
    import { LAYER_BG, LAYER_FG } from '~/core/constants';

    const props = defineProps({
        debug: { default: false },
        layer: { default: LAYER_FG },
        text: { default: 'Chase' },
        depthWrite: { default: true },
        depthTest: { default: true },
        renderOrder: { default: 1 },
        options: { default: () => ({}) },
    });

    const refText = ref(null);
    const visible = ref(false);

    const { object } = useThreeObject(null, { name: 'TextScaleIn', props: { v: visible } });

    const bounds = useBounds();
    const fontSize = computed(() => bounds.height * 0.3);

    const textOptions = reactive({
        fontSize,
        anchorX: 'center',
        anchorY: 'middle',
        color: 'white',
        ...props.options,
    });

    const onTextSync = () => {
        updateLetters();
        const { mesh } = refText.value;

        mesh.material.depthWrite = props.depthWrite;
        mesh.material.depthTest = props.depthTest;
        mesh.renderOrder = props.renderOrder;
    };

    const updateLetters = () => {
        refText.value.updateLetters();
    };

    let tl;
    const show = ({ delay = 0 } = {}) => {
        tl?.kill();

        tl = new gsap.timeline({
            delay,
            onStart: () => (visible.value = true),
            onComplete: () => (visible.value = false),
            onUpdate: updateLetters,
        });

        const inDur = 0.7;
        const outDur = 0.3;
        const outDelay = 0.9;
        const inEase = 'power3.out';
        const outEase = 'power2.in';
        const { width, height } = bounds;

        tl.addLabel('start', 0);
        tl.addLabel('end', outDelay);

        const { object, mesh, letters } = refText.value;

        tl.fromTo(
            object.scale,
            { x: 2, y: 2, z: 2 },
            { x: 1, y: 1, z: 1, duration: inDur, ease: inEase },
            'start'
        );
        tl.fromTo(
            mesh.scale,
            { x: 1, y: 1, z: 1 },
            { x: 0.9, y: 0.9, z: 0.9, duration: outDelay, ease: 'none' },
            `start`
        );
        tl.to(
            object.scale,
            { x: 1.5, y: 1.5, z: 1.5, duration: outDur, ease: 'back.in(1.5)' },
            `end`
        );

        const centerLetterIndex = Math.floor(letters.length / 2);

        tl.fromTo(letters, { alpha: 0 }, { alpha: 1, duration: inDur }, 'start');
        tl.to(letters, { alpha: 0, duration: 0.1 }, 'end+=0.2');

        letters.forEach((letter, i) => {
            const newIndex = i - centerLetterIndex;
            const letterDist = newIndex * height * 0.15;

            tl.fromTo(
                letter.position,
                { x: letterDist },
                { x: 0, duration: inDur, ease: inEase },
                `start`
            );
        });

        return tl;
    };

    const setPosition = () => {};

    const setBounds = () => {};

    watchEffect(setPosition);
    watchEffect(setBounds);

    defineExpose({ show, object, text: refText, updateLetters });
</script>
